import { getApiErrorCode, getApiErrorStatus } from "@/lib/api-error";
import { APICallError } from "ai";

/** The message of a refusal the company page knows how to name. */
export type CompanyRefusalKey =
  | "company.alerts.dailyLimitReached"
  | "company.alerts.tooManyRequests"
  | "company.alerts.noLicence"
  | "company.alerts.forbidden";

type Refusal = { status: number | null; code: string | null };

function codeOfBody(body: string | undefined): string | null {
  if (!body) return null;
  try {
    const parsed: unknown = JSON.parse(body);
    if (typeof parsed !== "object" || parsed === null) return null;
    const code = (parsed as { code?: unknown }).code;
    return typeof code === "string" ? code : null;
  } catch {
    // A refusal sent as plain text (a proxy's 429) names no code.
    return null;
  }
}

/**
 * The HTTP status and API `code` of a failed call, whichever client made it:
 * axios (`useApi`), the SSE reader (`SSEError`, `useApiStream`) or the AI
 * SDK's chat transport (`APICallError`, the AI cards).
 */
function readRefusal(error: unknown): Refusal {
  if (APICallError.isInstance(error)) {
    return {
      status: error.statusCode ?? null,
      code: codeOfBody(error.responseBody),
    };
  }
  const axiosStatus = getApiErrorStatus(error);
  if (axiosStatus !== null) {
    return { status: axiosStatus, code: getApiErrorCode(error) };
  }
  if (error instanceof Error) {
    const { status, code } = error as Error & { status?: unknown; code?: unknown };
    return {
      status: typeof status === "number" ? status : null,
      code: typeof code === "string" ? code : null,
    };
  }
  return { status: null, code: null };
}

/**
 * What to tell the user about a refused call on the company page, or null for
 * a failure that has no specific message (the generic error stays):
 * - the team's daily budget is spent (`rate_limited`, 429);
 * - the per-minute throttle answered (a bare 429): a minute fixes it;
 * - the licence lacks the page (`NO_LICENCE`, 403), or the selected team
 *   refuses it (any other 403: not a member, role, blocked team).
 */
export function companyRefusalKey(error: unknown): CompanyRefusalKey | null {
  const { status, code } = readRefusal(error);
  if (code === "rate_limited") return "company.alerts.dailyLimitReached";
  if (status === 429) return "company.alerts.tooManyRequests";
  if (status === 403) {
    return code === "NO_LICENCE"
      ? "company.alerts.noLicence"
      : "company.alerts.forbidden";
  }
  return null;
}
